// Constants
import { CHART_TYPE_DEFAULT, PARTNER_OPTION_DEFAULT } from 'ChartApp/ts/features/charts-shared/constants';
import { DataInLegendOption, FundChartOptionValue } from 'ChartApp/ts/enums/fund-chart-common.enum';
import { ChartDateZoom, NameInLegendOption } from 'ChartApp/ts/enums/chart-common.enum';
export const BASE_LEGEND_HEIGHT = 37;
export const ADDITIONAL_LEGEND_HEIGHT = 23;
export const TOOLTIP_STYLE_BOX = 'box';
// chart type constants
export const EMBED_TYPE_IMAGE_CONTENTS = 'image_contents';
export const DEFAULT_CROSSHAIR_OPTIONS = {
    color: '#808080',
    dashStyle: 'Solid',
    snap: false,
    width: 1,
};
export const HIGHCHARTS_FUND_DEFAULT_OPTIONS = {
    chart: {
        events: {},
        plotBackgroundColor: 'transparent',
        backgroundColor: 'transparent',
        plotBorderColor: '#e5e5e5',
        plotBorderWidth: 1,
        spacing: [1, 15, 1, 1],
        style: {
            fontFamily: 'Roboto Condensed',
        },
        panKey: 'shift',
        panning: {
            enabled: false,
            type: 'xy',
        },
        zooming: { type: 'xy', mouseWheel: false },
        // Disable Highcharts reset zoom button because we are using our own custom button
        resetZoomButton: {
            theme: {
                style: {
                    display: 'none',
                },
            },
        },
    },
    credits: {
        enabled: false,
    },
    exporting: { enabled: false },
    legend: { enabled: false },
    navigation: {
        annotationsOptions: {
            shapeOptions: {
                fill: 'rgba(0, 0, 0, 0)',
            },
        },
        bindingsClassName: 'toolset',
    },
    navigator: { enabled: false },
    stockTools: {
        gui: {
            enabled: false,
        },
    },
    plotOptions: {
        area: {
            fillOpacity: 0.25,
            softThreshold: false,
            threshold: 0,
        },
        line: {
            softThreshold: false,
            states: {
                hover: {
                    enabled: true,
                },
            },
        },
        flags: {
            allowOverlapX: true,
            allowPointSelect: true,
            color: '#fff',
            label: {
                connectorAllowed: false,
            },
            shape: 'circlepin',
            stacking: 'normal',
            stackDistance: 20,
            style: {
                color: '#fff',
            },
            tooltip: {
                footerFormat: '',
                headerFormat: '',
                pointFormat: '{point.text}',
            },
        },
        series: {
            states: {
                inactive: {
                    opacity: 1,
                },
            },
        },
    },
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    series: [],
    title: { text: '' },
    tooltip: {
        // We need to disable the tooltip's SVG rendered background
        // And provide HTML CSS styling on the .highcharts-tooltip>span
        // In order to have it render on top of the zoom directive that is HTML
        backgroundColor: undefined,
        borderColor: undefined,
        borderRadius: 0,
        borderWidth: 0,
        enabled: true,
        followPointer: true,
        hideDelay: 0,
        padding: 5,
        shadow: false,
        shared: false,
        split: false,
        useHTML: true,
        valueDecimals: 2,
    },
    xAxis: {
        className: 'chart-axis-x chart-axis-x-item',
        crosshair: {
            ...DEFAULT_CROSSHAIR_OPTIONS,
        },
        dateTimeLabelFormats: {
            minute: '%l%P',
            hour: '%l%P',
            day: '%b %e',
            week: '%b %e',
        },
        gridLineWidth: 1,
        labels: {
            style: {
                fontSize: '11',
                color: '#666',
            },
            y: 16,
        },
        lineWidth: 0,
        ordinal: false,
        tickAmount: 5,
        tickLength: 0,
        type: 'datetime',
    },
    yAxis: {
        custom: {
            allowNegativeLog: true,
        },
        className: 'chart-axis-y chart-axis-y-item',
        crosshair: {
            width: 0,
        },
        lineColor: '#e5e5e5',
        labels: {
            align: 'left',
            style: {
                fontSize: '11',
                color: '#666',
            },
            x: 5,
        },
        lineWidth: 0,
        // We need a small amount of minPadding for tooltips to work for
        // points on the bottom edge of the chart
        minPadding: 0.008,
        startOnTick: false,
        endOnTick: false,
        maxPadding: 0.3,
    },
};
// valid zoom options
export const VALID_FUND_CHART_DATE_ZOOM_OPTIONS = [
    { label: '1d', value: ChartDateZoom.CHART_ZOOM_ONE_DAY, enabled: true },
    { label: '5d', value: ChartDateZoom.CHART_ZOOM_FIVE_DAY, enabled: true },
    { label: '1m', value: ChartDateZoom.CHART_ZOOM_ONE_MONTH, enabled: true },
    { label: '3m', value: ChartDateZoom.CHART_ZOOM_THREE_MONTH, enabled: true },
    { label: '6m', value: ChartDateZoom.CHART_ZOOM_SIX_MONTH, enabled: true },
    { label: 'YTD', value: ChartDateZoom.CHART_ZOOM_YTD, enabled: true },
    { label: '1y', value: ChartDateZoom.CHART_ZOOM_ONE_YEAR, enabled: true },
    { label: '3y', value: ChartDateZoom.CHART_ZOOM_THREE_YEAR, enabled: true },
    { label: '5y', value: ChartDateZoom.CHART_ZOOM_FIVE_YEAR, enabled: true },
    { label: '10y', value: ChartDateZoom.CHART_ZOOM_TEN_YEAR, enabled: true },
    { label: 'Max', value: ChartDateZoom.CHART_ZOOM_MAX, enabled: true },
];
export const FUND_CHART_OPTIONS = [
    {
        label: 'Show Custom Colors',
        value: FundChartOptionValue.SHOW_CUSTOM_COLORS,
        presentationOnly: false,
        helpName: 'custom_colors_option',
    },
    {
        label: 'Show Forward Estimates',
        value: FundChartOptionValue.SHOW_FORWARD_ESTIMATES,
        presentationOnly: false,
    },
    {
        label: 'Show Units in Legend',
        value: FundChartOptionValue.SHOW_UNITS_IN_LEGEND,
        presentationOnly: false,
    },
    {
        label: 'Show US Recessions',
        value: FundChartOptionValue.SHOW_US_RECESSIONS,
        presentationOnly: false,
        helpName: 'show_us_recessions',
    },
    {
        label: 'Use Log Scale',
        value: FundChartOptionValue.USE_LOG_SCALE,
        trueValue: 'log',
        falseValue: 'linear',
        presentationOnly: false,
    },
    {
        label: 'Remove Value Flags',
        value: FundChartOptionValue.HIDE_VALUE_FLAGS,
        presentationOnly: false,
    },
    {
        label: 'Show Legend on Chart',
        value: FundChartOptionValue.LEGEND_ON_CHART,
        presentationOnly: true,
    },
    {
        label: 'Use Quote Style Legend',
        value: FundChartOptionValue.QUOTE_LEGEND,
        presentationOnly: true,
    },
];
export const DATA_IN_LEGEND_OPTIONS = [
    {
        label: 'Value Only',
        value: DataInLegendOption.VALUE,
    },
    {
        label: 'Annualized Value Only',
        value: DataInLegendOption.ANNUALIZED_VALUE,
        helpName: 'annualized_returns_chart_option',
    },
    {
        label: 'Value and Annualized Value',
        value: DataInLegendOption.VALUE_AND_ANNUALIZED_VALUE,
    },
    {
        label: 'None',
        value: DataInLegendOption.NONE,
    },
];
export const DATA_IN_FOOTER_OPTIONS = [
    {
        label: 'Show Data Source',
        value: FundChartOptionValue.SHOW_DATA_SOURCE,
        presentationOnly: true,
    },
    {
        label: 'Show Date Range',
        value: FundChartOptionValue.DISPLAY_DATE_RANGE,
        presentationOnly: true,
    },
    {
        label: 'Show Performance Disclosure',
        value: FundChartOptionValue.SHOW_PERFORMANCE_DISCLOSURE,
        presentationOnly: true,
    },
];
// Only related to 'currency_growth' Format
export const DEFAULT_CUSTOM_GROWTH_AMOUNT = 10000;
export const MIN_CUSTOM_GROWTH_AMOUNT = 1;
export const MAX_CUSTOM_GROWTH_AMOUNT = 1000000000;
// format constants
export const CHART_FORMAT_CURRENCY_GROWTH = 'currency_growth';
export const CHART_FORMAT_NORMALIZED = 'indexed';
export const CHART_FORMAT_PERCENT_OFF_HIGH = 'percent_off_high';
export const CHART_FORMAT_REAL = 'real';
export const FORMAT_DEFAULT = CHART_FORMAT_REAL;
export const FORMAT_OPTIONS = [
    {
        label: 'Original (Unchanged)',
        value: CHART_FORMAT_REAL,
    },
    {
        label: 'Normalized (% Change)',
        value: CHART_FORMAT_NORMALIZED,
    },
    {
        label: 'Growth (Custom)',
        value: CHART_FORMAT_CURRENCY_GROWTH,
    },
    {
        label: 'Percent Off High',
        value: CHART_FORMAT_PERCENT_OFF_HIGH,
    },
];
// nameInLegend options
export const NAME_IN_LEGEND_DEFAULT = NameInLegendOption.nameAndTicker;
// dataInLegend options
export const DATA_IN_LEGEND_DEFAULT = DataInLegendOption.VALUE;
// default metrics per security type for fund chart
export const DEFAULT_CALCS_FOR_SECURITYLIST_TYPE = {
    multi: { id: 'total_return_forward_adjusted_price', include: true },
    company: { id: 'price', include: true },
    fund: { id: 'total_return_forward_adjusted_price', include: true },
    index: { id: 'level', include: true },
    model_portfolio: { id: 'total_return_forward_adjusted_price', include: true },
    mutual_fund: { id: 'total_return_forward_adjusted_price', include: true },
    separate_account: { id: 'total_return_forward_adjusted_price', include: true },
};
// Chart scale types
export const SCALE_TYPE_LINEAR = 'linear';
export const SCALE_TYPE_LOG = 'log';
export const SCALE_TYPE_VALUES = [SCALE_TYPE_LINEAR, SCALE_TYPE_LOG];
export const SCALE_TYPE_DEFAULT = SCALE_TYPE_VALUES[0];
// 'splitType' state param options / defaults / values
export const SPLIT_TYPE_OPTIONS = [
    {
        label: 'Single Panel',
        value: 'single',
    },
    {
        label: 'Panel per Financial Metric',
        value: 'metric',
    },
    {
        label: 'Panel per Security',
        value: 'security',
    },
];
export const SPLIT_TYPE_VALUES = SPLIT_TYPE_OPTIONS.map((o) => o.value);
export const SPLIT_TYPE_DEFAULT = SPLIT_TYPE_VALUES[0];
// dateSelection
export const DATE_SELECTION_DEFAULT = 'range';
// correlations
export const SAVED_FUND_CHART_CORRELATIONS_INTEGER_FIELDS = ['correlationId', 'weightA', 'weightB', 'lagA', 'lagB'];
// saved fund chart
export const SAVED_CHART_NAME_FIELD = 'chartName';
export const SAVED_FUND_CHART_BOOLEAN_KEYS = [
    'displayDateRange',
    'legendOnChart',
    'quoteLegend',
    'recessions',
    'source',
    'units',
    'useCustomColors',
    'useEstimates',
    'hideValueFlags',
    'performanceDisclosure',
];
export const SAVED_FUND_CHART_VIEW_STATE_KEYS = [
    'axisExtremes',
    'calcs',
    'chartAnnotations',
    'chartType',
    'correlations',
    'customGrowthAmount',
    'dataInLegend',
    'dateSelection',
    'displayDateRange',
    'endDate',
    'format',
    'legendOnChart',
    'lineAnnotations',
    'nameInLegend',
    'note',
    'partner',
    'performanceDisclosure',
    'quoteLegend',
    'recessions',
    'scaleType',
    'securities',
    'securityGroup',
    'securitylistName',
    'securitylistSecurityId',
    'source',
    'splitType',
    'startDate',
    'title',
    'units',
    'useCustomColors',
    'useEstimates',
    'zoom',
    'hideValueFlags',
];
// state
const FUND_CHART_DEFAULT_CHART_OPTIONS = {
    [FundChartOptionValue.SHOW_CUSTOM_COLORS]: false,
    [FundChartOptionValue.DATA_IN_LEGEND]: DataInLegendOption.VALUE,
    [FundChartOptionValue.SHOW_FORWARD_ESTIMATES]: false,
    [FundChartOptionValue.NAME_IN_LEGEND]: NAME_IN_LEGEND_DEFAULT,
    [FundChartOptionValue.SHOW_UNITS_IN_LEGEND]: false,
    [FundChartOptionValue.SHOW_US_RECESSIONS]: false,
    [FundChartOptionValue.USE_LOG_SCALE]: SCALE_TYPE_DEFAULT,
    [FundChartOptionValue.HIDE_VALUE_FLAGS]: false,
};
export const FUND_CHART_SYSTEM_DEFAULT_STATE = {
    axisExtremes: {},
    calcs: [],
    chartAnnotations: [],
    chartData: {},
    chartId: null,
    chartType: CHART_TYPE_DEFAULT,
    correlations: [],
    customGrowthAmount: null,
    dateSelection: DATE_SELECTION_DEFAULT,
    displayDateRange: false,
    endDate: '',
    format: FORMAT_DEFAULT,
    legendOnChart: false,
    lineAnnotations: {},
    note: '',
    partner: PARTNER_OPTION_DEFAULT,
    performanceDisclosure: false,
    quoteLegend: false,
    securities: [],
    securityGroup: '',
    securitylistName: '',
    securitylistSecurityId: '',
    sortColumn: '',
    sortDirection: '',
    source: false,
    splitType: SPLIT_TYPE_DEFAULT,
    startDate: '',
    title: '',
    zoom: '',
    ...FUND_CHART_DEFAULT_CHART_OPTIONS,
};
export const FUND_CHART_DATA_KEYS = [
    'calcs',
    'chartId',
    'chartType',
    'correlations',
    'customGrowthAmount',
    'dataInLegend',
    'dateSelection',
    'displayDateRange',
    'endDate',
    'format',
    'legendOnChart',
    'lineAnnotations',
    'nameInLegend',
    'note',
    'partner',
    'performanceDisclosure',
    'quoteLegend',
    'recessions',
    'scaleType',
    'securities',
    'securityGroup',
    'securitylistName',
    'securitylistSecurityId',
    'source',
    'splitType',
    'startDate',
    'title',
    'units',
    'useCustomColors',
    'useEstimates',
    'zoom',
    'hideValueFlags',
];
// Chart annotations and axisExtremes are part of the saved App State so they persist but we don't include them
// in `FUND_CHART_DATA_KEYS` because changes to annotations or axisExtremes can never affect the data we
// receive from the backend, so we don't want those changes to trigger a data refresh.
export const FUND_CHART_STATE_KEYS = ['axisExtremes', 'chartAnnotations', ...FUND_CHART_DATA_KEYS];
// Fund charts cannot have annotations in the creator app
export const FUND_CHART_CREATOR_STATE_KEYS = FUND_CHART_DATA_KEYS;
export const FUND_CHART_APP_STATE_SLICE_KEY = 'fundChart';
// Chart dash styles
export const ANNOTATION_DASH_STYLE = 'Dash';
export const ESTIMATE_DASH_STYLE = 'Dot';
// Chart arrow
export const CHART_ARROW_HEIGHT = 16.5;
export const CHART_ARROW_PATH = ['M0', '8', 'L8', '0', 'L50', '0', 'L50', '16', 'L8', '16', 'Z'];
export const MIN_CHART_ARROW_WIDTH = 42;
// Time related
export const MULTI_YEAR_THRESHOLD = 3.5;
export const MULTI_MONTH_THRESHOLD = 2.5;
export const TIME_UNIT_SIZE = {
    second: 1000,
    minute: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000,
    year: 365.2425 * 24 * 60 * 60 * 1000,
};
// Tooltip styles
export const TOOLTIP_STYLE_BAR = 'bar';
// Annotations
export const ANNOTATION_AVG = 'avg';
export const ANNOTATION_DIVIDENDS = 'dividends';
export const ANNOTATION_EARNINGS_RESULTS = 'earnings_results';
export const ANNOTATION_FORCE_ORIGINAL = 'force_original';
export const ANNOTATION_LINE_COLOR = 'line_color';
export const ANNOTATION_LINE_WEIGHT = 'line_weight';
export const ANNOTATION_MAX = 'max';
export const ANNOTATION_MIN = 'min';
export const ANNOTATION_SPINOFFS = 'spinoffs';
export const ANNOTATION_SPLITS = 'splits';
export const SERIES_TYPE_FLAGS = 'flags';
export const ANNOTATION_FLAG_TITLE_MAP = {
    [ANNOTATION_DIVIDENDS]: 'D',
    [ANNOTATION_EARNINGS_RESULTS]: 'E',
    [ANNOTATION_SPLITS]: 'S',
    [ANNOTATION_SPINOFFS]: 'S',
};
export const ANNOTATION_LINE_LABEL_INFO = {
    [ANNOTATION_MIN]: {
        label: {
            prefix: 'Low: ',
            y: 12.5,
        },
        arrow: null,
    },
    [ANNOTATION_MAX]: {
        label: {
            prefix: 'Hi: ',
            y: -10.5,
        },
        arrow: null,
    },
    [ANNOTATION_AVG]: {
        label: null,
        arrow: {
            suffix: 'AVG',
        },
    },
};
// Fund Chart Line Width
export const FUND_CHART_LINE_WEIGHT_THIN = 'thin';
export const FUND_CHART_LINE_WEIGHT_MEDIUM = 'medium';
export const FUND_CHART_LINE_WEIGHT_THICK = 'thick';
export const LINE_WEIGHT_MAPPING = {
    [FUND_CHART_LINE_WEIGHT_THIN]: 1,
    [FUND_CHART_LINE_WEIGHT_MEDIUM]: 2,
    [FUND_CHART_LINE_WEIGHT_THICK]: 4,
};
// Line Width To Label Map
export const LINE_WEIGHT_LABEL_MAP = {
    [FUND_CHART_LINE_WEIGHT_THIN]: 'Thin',
    [FUND_CHART_LINE_WEIGHT_MEDIUM]: 'Medium',
    [FUND_CHART_LINE_WEIGHT_THICK]: 'Thick',
};
export const FUND_CHART_NO_DATA_HTML = `<div class="mt-2 mb-1 mr-2"> Get started.<br><span>Change or add items from left sidebar.</span></div>`;
